import { useState, useEffect, useCallback } from 'react';
import { exists, BaseDirectory } from '@tauri-apps/plugin-fs';

const isTauriEnv = typeof window !== 'undefined' && '__TAURI__' in window;

function sanitizeUrlToFilename(url: string): string {
  const fileName = url.split('/').pop() || '';
  return fileName.replace(/[\/:]/g, '_');
}

/**
 * Hook to check if a book's content is cached locally in the Tauri app.
 * Returns { isCached: boolean, isLoading: boolean, refresh: () => void }
 */
export function useIsBookCached(contentUrl: string | undefined): {
  isCached: boolean;
  isLoading: boolean;
  refresh: () => void;
} {
  const [isCached, setIsCached] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [version, setVersion] = useState(0);

  const refresh = useCallback(() => {
    setVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    if (!isTauriEnv || !contentUrl) {
      setIsLoading(false);
      setIsCached(false);
      return;
    }

    let mounted = true;

    const checkCache = async () => {
      setIsLoading(true);
      try {
        const sanitizedFileName = sanitizeUrlToFilename(contentUrl);
        const filePath = `readcloud/${sanitizedFileName}`;
        const fileExists = await exists(filePath, {
          baseDir: BaseDirectory.AppData,
        });
        if (mounted) {
          setIsCached(fileExists);
        }
      } catch {
        if (mounted) {
          setIsCached(false);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    checkCache();

    return () => {
      mounted = false;
    };
  }, [contentUrl, version]);

  return { isCached, isLoading, refresh };
}
